import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import type { HelpKey } from '@zt/help';
import { Badge, Button, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ServerEventRow, ServerEventsView } from '../lib/types';

/**
 * Sunucu taraflı olaylar (F5-12): GA4 Measurement Protocol, Meta Conversions API, TikTok Events API.
 * Gizli anahtarlar yalnızca yazılır (sunucu hiçbir zaman geri döndürmez); test olayı ve olay günlüğü.
 */

type Provider = ServerEventsView['providers'][number];
type ProviderKey = Provider['provider'];

const HELP: Record<ProviderKey, HelpKey> = {
  ga4: 'tracking.server_ga4',
  meta: 'tracking.server_meta',
  tiktok: 'tracking.server_tiktok',
};
const TONE = { live: 'moss', off: 'slate', needs_secret: 'amber', needs_id: 'slate' } as const;
const STATUS_TONE = { sent: 'moss', pending: 'amber', failed: 'rose', skipped: 'slate' } as const;

export function ServerEventsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['server-events', storeId],
    queryFn: () => api.get<ServerEventsView>(`/v1/admin/stores/${storeId}/server-events`),
  });
  const userData = useApiMutation(
    (v: boolean) => api.put(`/v1/admin/stores/${storeId}/server-events`, { userData: v }),
    { invalidate: [['server-events', storeId]], success: t('admin.tracking.server.saved') },
  );
  return (
    <Section
      title={t('admin.tracking.server.title')}
      description={t('admin.tracking.server.lead')}
      action={<HelpTip k="tracking.server_events" />}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-3">
            {q.data.providers.map((p) => (
              <ProviderCard key={`${storeId}-${p.provider}`} storeId={storeId} p={p} />
            ))}
          </div>
          <div className="mt-5 rounded-md border border-line px-4 py-3">
            <span className="flex items-center gap-2">
              <Switch
                checked={q.data.userData}
                disabled={!can('tracking.manage') || userData.isPending}
                onCheckedChange={(v) => userData.mutate(v)}
                label={t('admin.tracking.server.userData')}
              />
              <span className="text-sm font-medium">{t('admin.tracking.server.userData')}</span>
              <HelpTip k="tracking.server_user_data" />
            </span>
            <p className="mt-1 text-sm text-ink-2">{t('admin.tracking.server.userDataLead')}</p>
          </div>
        </>
      )}
    </Section>
  );
}

function ProviderCard({ storeId, p }: { storeId: string; p: Provider }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('tracking.manage');
  const [secret, setSecret] = useState('');
  const [testCode, setTestCode] = useState<string | null>(null);
  const code = testCode ?? p.testCode ?? '';
  const withTestCode = p.provider !== 'ga4';
  const save = useApiMutation(
    (v: { enabled: boolean; removeSecret?: boolean }) =>
      api.put(`/v1/admin/stores/${storeId}/server-events`, {
        [p.provider]: {
          enabled: v.enabled,
          ...(v.removeSecret ? { secret: null } : secret.trim() ? { secret: secret.trim() } : {}),
          ...(withTestCode ? { testCode: code.trim() || null } : {}),
        },
      }),
    {
      invalidate: [['server-events', storeId]],
      success: t('admin.tracking.server.saved'),
      onSuccess: () => {
        setSecret('');
        setTestCode(null);
      },
      silentFieldErrors: true,
    },
  );
  const test = useApiMutation(
    () =>
      api.post<{ ok: boolean; error: string | null; messages: string[] }>(
        `/v1/admin/stores/${storeId}/server-events/test`,
        { provider: p.provider },
      ),
    {
      onSuccess: (r) => {
        if (r.ok) toast.success(t('admin.tracking.server.testOk'));
        else
          toast.error(
            t('admin.tracking.server.testFailed', {
              reason: [r.error, ...r.messages].filter(Boolean).join(' · '),
            }),
          );
      },
    },
  );
  const field = (k: string) => fieldError(save.error, `${p.provider}.${k}`);
  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-line p-4"
      aria-label={t(`admin.tracking.server.providers.${p.provider}`)}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ enabled: p.enabled || p.status === 'needs_secret' });
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-medium">{t(`admin.tracking.server.providers.${p.provider}`)}</h3>
        <span className="flex items-center gap-1">
          <Badge tone={TONE[p.status]}>{t(`admin.tracking.server.status.${p.status}`)}</Badge>
          <HelpTip k={HELP[p.provider]} />
        </span>
      </div>
      {p.status === 'needs_id' ? (
        <p className="text-sm text-ink-2">{t(`admin.tracking.server.needsId.${p.provider}`)}</p>
      ) : (
        <>
          <p className="font-mono text-[0.8125rem] text-ink-2">{p.id}</p>
          <fieldset disabled={!canWrite} className="space-y-3">
            <Field label={t(`admin.tracking.server.secret.${p.provider}`)} error={field('secret')}>
              {(fp) => (
                <Input
                  {...fp}
                  type="password"
                  autoComplete="new-password"
                  spellCheck={false}
                  value={secret}
                  placeholder={p.hasSecret ? t('admin.tracking.server.secretSaved') : ''}
                  onChange={(e) => setSecret(e.target.value)}
                />
              )}
            </Field>
            {withTestCode && (
              <Field
                label={t('admin.tracking.server.testCode')}
                optionalLabel={t('common.optional')}
                error={field('testCode')}
                help={<HelpTip k="tracking.server_test_code" />}
              >
                {(fp) => (
                  <Input
                    {...fp}
                    value={code}
                    maxLength={40}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="TEST12345"
                    onChange={(e) => setTestCode(e.target.value)}
                  />
                )}
              </Field>
            )}
            <span className="flex items-center gap-2">
              <Switch
                checked={p.enabled}
                disabled={!canWrite || save.isPending || (!p.hasSecret && !secret.trim())}
                onCheckedChange={(v) => save.mutate({ enabled: v })}
                label={t('admin.tracking.server.enabled')}
              />
              <span className="text-sm">{t('admin.tracking.server.enabled')}</span>
            </span>
            {field('enabled') && <p className="text-sm text-rose">{field('enabled')}</p>}
          </fieldset>
          {canWrite && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={save.isPending || (!secret.trim() && testCode === null)}
              >
                {t('admin.tracking.server.save')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!p.hasSecret}
                loading={test.isPending}
                onClick={() => test.mutate(undefined)}
              >
                {t('admin.tracking.server.test')}
              </Button>
              <HelpTip k="tracking.server_test" />
              {p.hasSecret && (
                <ConfirmButton
                  variant="ghost"
                  message={t('admin.tracking.server.removeConfirm')}
                  onConfirm={() => save.mutateAsync({ enabled: false, removeSecret: true })}
                >
                  {t('admin.tracking.server.remove')}
                </ConfirmButton>
              )}
            </div>
          )}
          <p className="text-sm text-ink-3">
            {t('admin.tracking.server.last24h', {
              sent: p.last24h.sent,
              failed: p.last24h.failed,
              pending: p.last24h.pending,
              skipped: p.last24h.skipped,
            })}
          </p>
        </>
      )}
    </form>
  );
}

export function ServerEventLog({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState<'' | ServerEventRow['status']>('');
  const q = useQuery({
    queryKey: ['server-event-log', storeId, status],
    queryFn: () =>
      api.get<ServerEventRow[]>(
        `/v1/admin/server-events?storeId=${storeId}${status ? `&status=${status}` : ''}`,
      ),
  });
  const retry = useApiMutation((id: string) => api.post(`/v1/admin/server-events/${id}/retry`), {
    invalidate: [['server-event-log', storeId]],
    success: t('admin.tracking.server.retried'),
  });
  return (
    <Section
      title={t('admin.tracking.server.logTitle')}
      description={t('admin.tracking.server.logLead')}
      action={<HelpTip k="tracking.server_log" />}
    >
      <Field label={t('admin.tracking.server.filter')} className="mb-3 max-w-xs">
        {(fp) => (
          <Select {...fp} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">{t('admin.tracking.server.all')}</option>
            {(['pending', 'sent', 'failed', 'skipped'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`admin.tracking.server.eventStatus.${s}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.tracking.server.logEmpty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.tracking.server.logTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colTime')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colProvider')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colEvent')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colOrder')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colStatus')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.tracking.server.colDetail')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.createdAt, true)}</td>
                  <td className="px-3 py-2">{t(`admin.tracking.server.short.${r.provider}`)}</td>
                  <td className="px-3 py-2">{t(`admin.tracking.server.events.${r.eventName}`)}</td>
                  <td className="px-3 py-2">
                    {r.orderId && r.orderNumber ? (
                      <Link
                        to="/siparisler/$id"
                        params={{ id: r.orderId }}
                        className="text-pine hover:underline"
                      >
                        {r.orderNumber}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATUS_TONE[r.status]}>
                      {t(`admin.tracking.server.eventStatus.${r.status}`)}
                    </Badge>
                    {r.attempts > 1 && (
                      <span className="block text-xs text-ink-3">
                        {t('admin.tracking.server.attempts', { n: r.attempts })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.lastError && <span className="block text-ink-2">{errorText(t, r.lastError)}</span>}
                    <details>
                      <summary className="cursor-pointer text-pine">
                        {t('admin.tracking.server.payload')}
                      </summary>
                      <pre className="mt-1 max-h-64 max-w-md overflow-auto rounded bg-surface-2 p-2 text-xs">
                        {JSON.stringify(r.payload, null, 2)}
                      </pre>
                    </details>
                    {(r.status === 'failed' || r.status === 'skipped') && can('tracking.manage') && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="mt-1"
                        disabled={retry.isPending}
                        onClick={() => retry.mutate(r.id)}
                      >
                        {t('admin.tracking.server.retry')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

const KNOWN = ['no_client_id', 'no_match_keys', 'disabled', 'timeout', 'network', 'redirect'] as const;
function errorText(t: ReturnType<typeof useT>, code: string) {
  return (KNOWN as readonly string[]).includes(code) ? t(`admin.tracking.server.errors.${code}`) : code;
}
